import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { supabase, FILES_BUCKET } from '@/lib/supabase';
import { useData } from '@/context/DataContext';
import { useApp } from '@/context/AppContext';
import Modal from '@/components/base/Modal';
import { Field, Select } from '@/components/base/FormField';
import { fileCategories } from '@/types';
import { formatBytes } from '@/lib/format';

interface FileUploadModalProps {
  open: boolean;
  onClose: () => void;
}

const cancelClass =
  'cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100';
const saveClass =
  'cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60';

export default function FileUploadModal({ open, onClose }: FileUploadModalProps) {
  const { customers, projects, refresh } = useData();
  const { showToast } = useApp();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState('Diğer');
  const [relatedType, setRelatedType] = useState('');
  const [relatedId, setRelatedId] = useState('');
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFile(null);
    setCategory('Diğer');
    setRelatedType('');
    setRelatedId('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] ?? null);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!file) {
      showToast('Lütfen bir dosya seçin');
      return;
    }
    setBusy(true);
    try {
      const ext = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() ?? '' : '';
      const safeExt = ext && ext.length <= 12 ? ext : '';
      const uuid =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const path = `${uuid}${safeExt ? '.' + safeExt : ''}`;

      const { error: upError } = await supabase.storage
        .from(FILES_BUCKET)
        .upload(path, file, { cacheControl: '3600', upsert: false });
      if (upError) throw upError;

      const { error: insError } = await supabase.from('3dmachine_files').insert({
        name: file.name,
        category: category || 'Diğer',
        related_type: relatedType || null,
        related_id: relatedId || null,
        size: formatBytes(file.size),
        uploaded_date: new Date().toISOString().slice(0, 10),
        storage_path: path,
      });
      if (insError) throw insError;

      showToast('Dosya yüklendi');
      reset();
      onClose();
      refresh();
    } catch (err) {
      showToast('Yüklenemedi: ' + (err instanceof Error ? err.message : 'bilinmeyen hata'));
    } finally {
      setBusy(false);
    }
  };

  const customerOptions = customers.map((c) => (
    <option key={c.id} value={c.id}>
      {c.name}
    </option>
  ));
  const projectOptions = projects.map((p) => (
    <option key={p.id} value={p.id}>
      {p.name}
    </option>
  ));

  return (
    <Modal open={open} onClose={close} title="Dosya Yükle" subtitle="Dosyayı seçip bilgilerini girin">
      <form onSubmit={handleSubmit} className="space-y-4">
        <input ref={fileRef} type="file" onChange={onPick} className="hidden" />

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-background-300 bg-background-100/50 px-4 py-8 text-center transition-colors hover:border-primary-300 hover:bg-background-100"
        >
          <i className="ri-upload-cloud-2-line text-3xl text-primary-500" />
          {file ? (
            <>
              <p className="max-w-full truncate text-sm font-semibold text-foreground-900">{file.name}</p>
              <p className="text-xs text-foreground-500">{formatBytes(file.size)} · Değiştirmek için tıklayın</p>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-foreground-800">Dosya seçmek için tıklayın</p>
              <p className="text-xs text-foreground-500">PDF, çizim, görsel, video vb. desteklenir</p>
            </>
          )}
        </button>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Kategori">
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {fileCategories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </Select>
          </Field>

          <Field label="Bağlantı">
            <Select value={relatedType} onChange={(e) => { setRelatedType(e.target.value); setRelatedId(''); }}>
              <option value="">Bağlantı yok</option>
              <option value="Müşteri">Müşteri</option>
              <option value="Proje">Proje</option>
            </Select>
          </Field>

          {relatedType === 'Müşteri' && (
            <Field label="Müşteri Seç" className="sm:col-span-2">
              <Select value={relatedId} onChange={(e) => setRelatedId(e.target.value)}>
                <option value="">Seçiniz</option>
                {customerOptions}
              </Select>
            </Field>
          )}

          {relatedType === 'Proje' && (
            <Field label="Proje Seç" className="sm:col-span-2">
              <Select value={relatedId} onChange={(e) => setRelatedId(e.target.value)}>
                <option value="">Seçiniz</option>
                {projectOptions}
              </Select>
            </Field>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={close} className={cancelClass}>
            Vazgeç
          </button>
          <button type="submit" disabled={busy || !file} className={saveClass}>
            {busy ? 'Yükleniyor...' : 'Yükle'}
          </button>
        </div>
      </form>
    </Modal>
  );
}